import { useMemo } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { IntlShape, useIntl } from 'react-intl';
import { useProject } from 'store/project';
import {
  DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW,
  DEFAULT_MESSAGE_SECTION_CONFIG_ON_VIEW,
  issueAttachmentsConfigFromOnView,
  messageSectionConfigFromOnView,
  sectionConfigFromOnView,
  toIssueAttachmentsConfigOnView,
  toMessageSectionConfigOnView,
  toSectionConfigOnView,
} from 'shared/domain/report/mapping/reportConfigOnView';
import {
  REPORT_FIELDS_CONTENT_LABELS,
  ReportType,
} from 'shared/domain/report/types/model/common';
import { isReportFieldsContent } from 'shared/domain/report/types/model/reportConfigGuards';
import {
  BaseReportOnView,
  IssueReportOnView,
} from 'shared/domain/report/types/onView/common';
import { SingleInspectionReportOnView } from 'shared/domain/report/types/onView/singleInspectionReportOnView';
import { getDefaultReportLocaleOption } from 'shared/utils/locale/reportLanguageDisplay';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { LocaleCountryAndLanguageCode } from 'shared/types/locale';
import type { SavedReportConfig } from 'services/storage/pdfReportPreferencesStorage';
import { getAvailableFieldsMap } from '../reportHelpers';

export interface BaseReportInitialValues extends BaseReportOnView {}

export interface IssueReportInitialValues extends IssueReportOnView {}

export type InspectionReportInitialValues = Omit<
  SingleInspectionReportOnView,
  'inspection'
>;

function savedConfigToBaseOnView(
  saved: SavedReportConfig,
  intl: IntlShape
): BaseReportInitialValues {
  return {
    recipients: [],
    name: '',
    message: '',
    // The comment always goes into the email notification; the in-PDF toggle is
    // the only user-controlled part (off by default).
    messagePresentation: {
      inEmailNotification: true,
      inPdfReport: saved.messagePresentation.inPdfReport,
    },
    localeCode: getDefaultReportLocaleOption(
      LocalStorageManager.getLastReportLocale()
    ),
    issueAttachments: toIssueAttachmentsConfigOnView(
      intl,
      saved.issueAttachments
    ),
    showFileNames: saved.showFileNames,
    showFileDescriptions: saved.showFileDescriptions,
    showActiveLinks: saved.showActiveLinks,
    showPhoneNumbers: saved.showPhoneNumbers,
    showUserEmails: saved.showUserEmails,
  };
}

export function createBaseReportInitialValues(
  reportType: ReportType,
  intl: IntlShape
): BaseReportInitialValues {
  LocalStorageManager.migrateLegacyReportLocalStorage(reportType);
  const saved = LocalStorageManager.getSavedReportConfig(reportType);
  return savedConfigToBaseOnView(saved, intl);
}

export function createIssueReportInitialValues(
  reportType: ReportType,
  intl: IntlShape,
  projectId: string,
  processId?: string
): IssueReportInitialValues {
  LocalStorageManager.migrateLegacyReportLocalStorage(reportType);
  const saved = LocalStorageManager.getSavedReportConfig(reportType);
  const base = savedConfigToBaseOnView(saved, intl);
  return {
    ...base,
    fieldsContent: {
      _id: LocalStorageManager.getReportFieldsContent(reportType),
      label: formatTranslation(
        intl,
        unsafeTranslationKey(
          REPORT_FIELDS_CONTENT_LABELS[
            LocalStorageManager.getReportFieldsContent(reportType)
          ]
        )
      ),
    },
    selectedFields: LocalStorageManager.getReportSelectedFields(
      reportType,
      intl,
      projectId,
      getAvailableFieldsMap(),
      processId
    ),
    linkedDocumentation: toSectionConfigOnView(
      intl,
      saved.linkedDocumentation
    ),
    events: toMessageSectionConfigOnView(intl, saved.events),
    incomingEmailMessages: toMessageSectionConfigOnView(
      intl,
      saved.incomingEmailMessages
    ),
    workflowDecisions: toMessageSectionConfigOnView(
      intl,
      saved.workflowDecisions
    ),
  };
}

export function createInspectionReportInitialValues(
  intl: IntlShape,
  projectId: string
): InspectionReportInitialValues {
  LocalStorageManager.migrateLegacyReportLocalStorage(ReportType.inspection);
  const contentConfig =
    LocalStorageManager.getSavedInspectionReportContentConfig();
  const saved = LocalStorageManager.getSavedReportConfig(ReportType.inspection);
  const base = savedConfigToBaseOnView(saved, intl);
  const fieldsContentId = LocalStorageManager.getReportFieldsContent(
    ReportType.inspection
  );

  return {
    ...base,
    showFileNames: contentConfig.fileTextMetadata.showFileNames,
    showFileDescriptions: contentConfig.fileTextMetadata.showFileDescriptions,
    showActiveLinks: contentConfig.fileTextMetadata.showActiveLinks,
    fieldsContent: {
      _id: fieldsContentId,
      label: formatTranslation(
        intl,
        unsafeTranslationKey(REPORT_FIELDS_CONTENT_LABELS[fieldsContentId])
      ),
    },
    selectedFields: LocalStorageManager.getReportSelectedFields(
      ReportType.inspection,
      intl,
      projectId,
      getAvailableFieldsMap()
    ),
    templateSectionEnabled: contentConfig.templateSectionEnabled,
    checklistSectionEnabled: contentConfig.checklistSectionEnabled,
    issueDataSectionEnabled: contentConfig.issueDataSectionEnabled,
    inspectionInfo: contentConfig.inspectionInfo,
    summary: contentConfig.summary,
    signatures: contentConfig.signatures,
    generalStandard: contentConfig.generalStandard,
    generalStandardAttachments: toSectionConfigOnView(
      intl,
      contentConfig.generalStandardAttachments
    ),
    sectionDescriptions: contentConfig.sectionDescriptions,
    sectionAttachments: toSectionConfigOnView(
      intl,
      contentConfig.sectionAttachments
    ),
    controlledElementStandard: contentConfig.controlledElementStandard,
    controlledElementStandardAttachments: toSectionConfigOnView(
      intl,
      contentConfig.controlledElementStandardAttachments
    ),
    controlledElementAttachments: toSectionConfigOnView(
      intl,
      contentConfig.controlledElementAttachments
    ),
    showNotApplicable: contentConfig.showNotApplicable,
    showUnchecked: contentConfig.showUnchecked,
    linkToHustro: contentConfig.linkToHustro,
  };
}

export function useBaseReportInitialValues(
  reportType: ReportType,
  deps: unknown[] = []
): BaseReportInitialValues {
  const intl = useIntl();

  return useMemo(
    () => createBaseReportInitialValues(reportType, intl),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, reportType, ...deps]
  );
}

export function useIssueReportInitialValues(
  reportType: ReportType,
  processId?: string,
  deps: unknown[] = []
): IssueReportInitialValues {
  const intl = useIntl();
  const project = useProject();

  return useMemo(
    () =>
      createIssueReportInitialValues(
        reportType,
        intl,
        project._id,
        processId
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, reportType, project._id, processId, ...deps]
  );
}

export function useInspectionReportInitialValues(
  deps: unknown[] = []
): InspectionReportInitialValues {
  const intl = useIntl();
  const project = useProject();

  return useMemo(
    () => createInspectionReportInitialValues(intl, project._id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, project._id, ...deps]
  );
}

export function saveBaseReportValues(
  reportType: ReportType,
  values: BaseReportOnView
): void {
  LocalStorageManager.saveLastReportLocale(
    values.localeCode._id as LocaleCountryAndLanguageCode
  );
  LocalStorageManager.saveReportConfig(reportType, {
    messagePresentation: values.messagePresentation,
    issueAttachments: issueAttachmentsConfigFromOnView(values.issueAttachments),
    showPhoneNumbers: values.showPhoneNumbers,
    showUserEmails: values.showUserEmails,
    showFileNames: values.showFileNames,
    showFileDescriptions: values.showFileDescriptions,
    showActiveLinks: values.showActiveLinks,
  });
}

function saveInspectionBaseReportValues(values: BaseReportOnView): void {
  LocalStorageManager.saveLastReportLocale(
    values.localeCode._id as LocaleCountryAndLanguageCode
  );
  LocalStorageManager.saveReportConfig(ReportType.inspection, {
    messagePresentation: values.messagePresentation,
    issueAttachments: issueAttachmentsConfigFromOnView(values.issueAttachments),
    showPhoneNumbers: values.showPhoneNumbers,
    showUserEmails: values.showUserEmails,
  });
}

export function saveInspectionReportValues(
  projectId: string,
  values: SingleInspectionReportOnView
): void {
  saveInspectionBaseReportValues(values);
  LocalStorageManager.saveInspectionReportContentConfig({
    templateSectionEnabled: values.templateSectionEnabled,
    checklistSectionEnabled: values.checklistSectionEnabled,
    issueDataSectionEnabled: values.issueDataSectionEnabled,
    inspectionInfo: values.inspectionInfo,
    summary: values.summary,
    signatures: values.signatures,
    generalStandard: values.generalStandard,
    generalStandardAttachments: sectionConfigFromOnView(
      values.generalStandardAttachments
    ),
    sectionDescriptions: values.sectionDescriptions,
    sectionAttachments: sectionConfigFromOnView(values.sectionAttachments),
    controlledElementStandard: values.controlledElementStandard,
    controlledElementStandardAttachments: sectionConfigFromOnView(
      values.controlledElementStandardAttachments
    ),
    controlledElementAttachments: sectionConfigFromOnView(
      values.controlledElementAttachments
    ),
    showNotApplicable: values.showNotApplicable,
    showUnchecked: values.showUnchecked,
    linkToHustro: values.linkToHustro,
    fileTextMetadata: {
      showFileNames: values.showFileNames,
      showFileDescriptions: values.showFileDescriptions,
      showActiveLinks: values.showActiveLinks,
    },
  });
  if (isReportFieldsContent(values.fieldsContent._id)) {
    LocalStorageManager.setReportFieldsContent(
      ReportType.inspection,
      values.fieldsContent._id
    );
  }
  LocalStorageManager.setReportSelectedFields(
    ReportType.inspection,
    projectId,
    values.selectedFields.map((field) => field._id).join(',')
  );
}

export function saveIssueReportValues(
  reportType: ReportType,
  projectId: string,
  values: IssueReportOnView,
  processId?: string
): void {
  saveBaseReportValues(reportType, values);
  LocalStorageManager.saveReportConfig(reportType, {
    linkedDocumentation: sectionConfigFromOnView(values.linkedDocumentation),
    events: messageSectionConfigFromOnView(values.events),
    incomingEmailMessages: messageSectionConfigFromOnView(
      values.incomingEmailMessages
    ),
    workflowDecisions: messageSectionConfigFromOnView(values.workflowDecisions),
  });
  if (isReportFieldsContent(values.fieldsContent._id)) {
    LocalStorageManager.setReportFieldsContent(
      reportType,
      values.fieldsContent._id
    );
  }
  LocalStorageManager.setReportSelectedFields(
    reportType,
    projectId,
    values.selectedFields.map((field) => field._id).join(','),
    processId
  );
}

// Re-export defaults for contentSelection initial state
export {
  DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW,
  DEFAULT_MESSAGE_SECTION_CONFIG_ON_VIEW
};

